import { create } from 'zustand';
import { Tag, TagCreate, TagUpdate, TagResponse } from '../types';
import { tagService } from '../services/tags-api';
import { toast } from 'sonner';

interface TagStore {
  tags: Tag[];
  loading: boolean;
  error: Error | null;
  lastFetchTimestamp: number;
  isFetching: boolean;
  meta: {
    total: number;
    skip: number;
    limit: number;
    has_more: boolean;
  };
  CACHE_DURATION: number;
  fetchTags: (params?: { skip?: number; limit?: number; force?: boolean }) => Promise<void>;
  createTag: (data: TagCreate) => Promise<Tag>;
  updateTag: (id: number, data: TagUpdate) => Promise<Tag>;
  deleteTag: (id: number) => Promise<void>;
  toggleFavorite: (id: number) => Promise<void>;
  getTagsFromCache: () => Tag[];
  getTagsByIds: (ids: number[]) => Tag[];
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache duration
const TAGS_CACHE_KEY = 'easylinks_tags_cache';
const TAGS_META_CACHE_KEY = 'easylinks_tags_meta_cache';

// Helper function to get tags from localStorage
const getTagsFromLocalStorage = (): { tags: Tag[]; meta: TagStore['meta'] | null } => {
  try {
    const cachedTags = localStorage.getItem(TAGS_CACHE_KEY);
    const cachedMeta = localStorage.getItem(TAGS_META_CACHE_KEY);
    return {
      tags: cachedTags ? JSON.parse(cachedTags) : [],
      meta: cachedMeta ? JSON.parse(cachedMeta) : null
    };
  } catch (error) {
    console.error('Error reading tags from localStorage:', error);
    return { tags: [], meta: null };
  }
};

// Helper function to save tags to localStorage
const saveTagsToLocalStorage = (tags: Tag[], meta: TagStore['meta']) => {
  try {
    if (!Array.isArray(tags)) {
      console.error('Attempted to save non-array tags to localStorage:', tags);
      return;
    }
    localStorage.setItem(TAGS_CACHE_KEY, JSON.stringify(tags));
    localStorage.setItem(TAGS_META_CACHE_KEY, JSON.stringify(meta));
  } catch (error) {
    console.error('Error saving tags to localStorage:', error);
  }
};

const useTagStore = create<TagStore>((set, get) => ({
  tags: [],
  loading: false,
  error: null,
  lastFetchTimestamp: 0,
  isFetching: false,
  meta: {
    total: 0,
    skip: 0,
    limit: 10,
    has_more: false
  },
  CACHE_DURATION,

  fetchTags: async (params = {}) => {
    const state = get();
    const now = Date.now();
    const isExpired = now - state.lastFetchTimestamp > CACHE_DURATION;
    
    // Return cached data if available and not expired, unless force refresh is requested
    if (!params.force && !isExpired && state.tags.length > 0) {
      return;
    }

    // Prevent concurrent fetches
    if (state.isFetching) {
      return;
    }

    try {
      set({ loading: true, error: null, isFetching: true });
      const response = await tagService.getTags(params);
      set({ 
        tags: response.items,
        meta: {
          total: response.total,
          skip: response.skip,
          limit: response.limit,
          has_more: response.has_more
        },
        lastFetchTimestamp: now
      });
    } catch (error) {
      console.error('Error fetching tags:', error);
      set({ error: error as Error });
      throw error;
    } finally {
      set({ loading: false, isFetching: false });
    }
  },

  createTag: async (data) => {
    try {
      const newTag = await tagService.createTag(data);
      set(state => ({
        tags: [...state.tags, newTag],
        meta: {
          ...state.meta,
          total: state.meta.total + 1
        }
      }));
      return newTag;
    } catch (error) {
      set({ error: error as Error });
      throw error;
    }
  },

  updateTag: async (id, data) => {
    try {
      const updatedTag = await tagService.updateTag(id, data);
      set(state => ({
        tags: state.tags.map(tag => tag.id === id ? updatedTag : tag)
      }));
      return updatedTag;
    } catch (error) {
      set({ error: error as Error });
      throw error;
    }
  },

  deleteTag: async (id) => {
    try {
      await tagService.deleteTag(id);
      set(state => ({
        tags: state.tags.filter(tag => tag.id !== id),
        meta: {
          ...state.meta,
          total: state.meta.total - 1
        }
      }));
    } catch (error) {
      set({ error: error as Error });
      throw error;
    }
  },

  toggleFavorite: async (id) => {
    try {
      const updatedTag = await tagService.toggleFavorite(id);
      set(state => ({
        tags: state.tags.map(tag => tag.id === id ? updatedTag : tag)
      }));
    } catch (error) {
      set({ error: error as Error });
      throw error;
    }
  },

  getTagsFromCache: () => {
    const state = get();
    const now = Date.now();
    if (now - state.lastFetchTimestamp > state.CACHE_DURATION) {
      state.fetchTags();
    }
    return state.tags;
  },

  getTagsByIds: (ids) => {
    return get().tags.filter(tag => ids.includes(tag.id));
  }
}));

export default useTagStore; 